import React from 'react';
import {
    Box,
    Card,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Stack,
    Alert,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Login as CheckInIcon,
    Logout as CheckOutIcon,
    AccessTimeFilled as ClockIcon,
    CheckCircleOutline as DoneIcon,
    LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useAttendance } from '../hooks/useAttendance';
import { formatToUIDateTimeLong, getBrowserLocale, formatDurationHrsMins } from '../utils/dateUtils';

interface Props {
    facilityId: string;
}

function formatElapsed(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

function formatTime(isoStr: string | null | undefined): string {
    if (!isoStr) return '—';
    // If it's a local ISO string (no 'Z'), parse as local Date to avoid global UTC shift
    if (typeof isoStr === 'string' && !isoStr.endsWith('Z')) {
        const localDate = new Date(isoStr);
        return formatToUIDateTimeLong(localDate);
    }
    return formatToUIDateTimeLong(isoStr);
}

const AttendanceWidget: React.FC<Props> = ({ facilityId }) => {
    const {
        session,
        isLoading,
        isActionLoading,
        error,
        elapsed,
        caregiverId,
        handleCheckIn,
        handleCheckOut,
    } = useAttendance(facilityId);
    const [currentTime, setCurrentTime] = React.useState(new Date());
    const [checkoutErrorOpen, setCheckoutErrorOpen] = React.useState(false);

    React.useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const getCheckedInFacilityName = () => {
        if (!session?.facilityId) return "the original facility";

        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const userObj = JSON.parse(userStr);
                if (userObj.facility && Array.isArray(userObj.facility)) {
                    const fac = userObj.facility.find((f: any) => f.id === session.facilityId);
                    if (fac && fac.name) {
                        return fac.name;
                    }
                }
            }

            // Fallback: check checkedInFacilityName from localStorage if set during check-in
            const cachedName = localStorage.getItem("checkedInFacilityName");
            if (cachedName) return cachedName;
        } catch (e) {
            console.error("Failed to parse user for facility name", e);
        }
        return "the original facility";
    };

    const checkedInFacilityName = getCheckedInFacilityName();

    const onCheckOutClick = () => {
        if (session && session.facilityId && session.facilityId !== facilityId) {
            setCheckoutErrorOpen(true);
            return;
        }
        handleCheckOut();
    };


    const isCheckedIn = session?.status === 'CHECKED_IN';
    const isCompleted = session?.status === 'COMPLETED';

    if (isLoading) {
        return (
            <Card
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: '20px',
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: 180,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress size={28} />
            </Card>
        );
    }

    return (
        <Card
            elevation={0}
            sx={{
                p: 3,
                borderRadius: '20px',
                border: '1px solid',
                borderColor: isCheckedIn ? 'success.light' : 'divider',
                background: isCheckedIn
                    ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                    : isCompleted
                        ? 'linear-gradient(135deg, #fafcff 0%, #f0f7fc 100%)'
                        : '#fff',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
            }}
        >
            {/* Pulsing dot when checked in */}
            {isCheckedIn && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        boxShadow: '0 0 0 0 rgba(34,197,94,0.4)',
                        animation: 'pulse-ring 1.5s ease-out infinite',
                        '@keyframes pulse-ring': {
                            '0%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.4)' },
                            '70%': { boxShadow: '0 0 0 10px rgba(34,197,94,0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0)' },
                        },
                    }}
                />
            )}

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        p: 1.25,
                        borderRadius: '12px',
                        bgcolor: isCheckedIn ? 'success.main' : isCompleted ? 'info.main' : 'primary.main',
                        color: '#fff',
                        display: 'flex',
                    }}
                >
                    {isCheckedIn ? <ClockIcon sx={{ fontSize: 20 }} /> : isCompleted ? <DoneIcon sx={{ fontSize: 20 }} /> : <CheckInIcon sx={{ fontSize: 20 }} />}
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        My Attendance
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Today - {currentTime.toLocaleDateString(getBrowserLocale(), { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Status info */}
            {!caregiverId ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2 }}>
                    User information not available.
                </Typography>
            ) : isCheckedIn ? (
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Check-In Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                            {formatTime(session!.checkIn)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            py: 2,
                            px: 2,
                            borderRadius: '12px',
                            bgcolor: 'rgba(34,197,94,0.1)',
                            border: '1px solid rgba(34,197,94,0.2)',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '80px',
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: 'success.dark',
                                letterSpacing: '0.08em',
                                fontVariantNumeric: 'tabular-nums',
                                lineHeight: 1
                            }}
                        >
                            {formatElapsed(elapsed)}
                        </Typography>
                    </Box>
                    {/* GPS chip — disabled until location feature is enabled
          {(session?.latitude) && (
            <Tooltip title={`GPS: ${session.latitude?.toFixed(4)}, ${session.longitude?.toFixed(4)}`} arrow>
              <Chip
                icon={<LocationIcon sx={{ fontSize: '14px !important' }} />}
                label="Location Captured"
                size="small"
                color="success"
                variant="outlined"
                sx={{ alignSelf: 'flex-start', height: 22, fontSize: '0.7rem' }}
              />
            </Tooltip>
          )}
          */}
                </Stack>
            ) : isCompleted ? (
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Check-In</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{session?.checkIn ? formatTime(session.checkIn) : '—'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Check-Out</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{session?.checkOut ? formatTime(session.checkOut) : '—'}</Typography>
                    </Box>
                    <Box
                        sx={{
                            py: 1,
                            px: 2,
                            borderRadius: '12px',
                            bgcolor: 'rgba(14,165,233,0.08)',
                            border: '1px solid rgba(14,165,233,0.2)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 600 }}>Total Hours</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'info.dark' }}>
                            {formatDurationHrsMins(session?.totalHours)}
                        </Typography>
                    </Box>
                    <Chip label="Shift Complete" size="small" color="info" variant="outlined" sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
                </Stack>
            ) : (
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        You have not checked in yet today.
                    </Typography>
                </Box>
            )}

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', py: 0.5, fontSize: '0.8rem' }}>
                    {error}
                </Alert>
            )}

            {/* Action Button */}
            {!isCompleted && (
                <Button
                    fullWidth
                    variant="contained"
                    color={isCheckedIn ? 'error' : 'success'}
                    onClick={isCheckedIn ? onCheckOutClick : handleCheckIn}
                    disabled={isActionLoading || !caregiverId}
                    startIcon={
                        isActionLoading ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : isCheckedIn ? (
                            <CheckOutIcon />
                        ) : (
                            <CheckInIcon />
                        )
                    }
                    sx={{
                        borderRadius: '12px',
                        py: 1.25,
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        boxShadow: 'none',
                        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
                    }}
                >
                    {isActionLoading ? 'Processing…' : isCheckedIn ? 'Check Out' : 'Check In'}
                </Button>
            )}

            {/* Validation Dialog */}
            <Dialog
                open={checkoutErrorOpen}
                onClose={() => setCheckoutErrorOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        maxWidth: '450px',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: '#FEF3C7', // light amber/yellow
                    color: '#B45309',   // dark amber text
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 3,
                    py: 2,
                    fontWeight: 700,
                    fontSize: '1.1rem'
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#FDE68A',
                            color: '#D97706',
                            borderRadius: '8px',
                            p: 0.5
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </Box>
                    Checkout Not Available
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 3, mt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6, mb: 3, fontSize: '0.95rem' }}>
                        You are currently checked in at <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>{checkedInFacilityName}</Box>.
                        Checkout must be performed from the same facility where the check-in was completed. Please switch to <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>{checkedInFacilityName}</Box> and proceed with Check Out.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pb: 3, px: 3, pt: 0 }}>
                    <Button
                        onClick={() => setCheckoutErrorOpen(false)}
                        variant="contained"
                        fullWidth
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.2,
                            bgcolor: '#0077B6',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#005f92',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default AttendanceWidget;
